import {
  mapFromControl,
  mapFromControlContext,
  mapFromControlDashboard,
  mapFromControls,
  mapFromControlScene,
  mapToControl,
} from 'mappings/controlMappings';
import { mapFromDashboard } from 'mappings/dashboardMapping';
import Control, { ControlDashboard } from 'models/control';
import ControlContext from 'models/controlContext';
import ControlScene from 'models/controlScene';
import Dashboard from 'models/dashboard';
import ControlContextDTO from 'models/dto/controlContextDTO';
import ControlDTO, { ControlDashboardDTO } from 'models/dto/controlDTO';
import ControlSceneDTO from 'models/dto/controlSceneDTO';
import { ControlThemeInheritInfoDTO } from 'models/dto/controlThemeInheritInfoDTO';
import DashboardDTO from 'models/dto/dashboardDTO';
import EntityRelationDTO from 'models/dto/entityRelationDTO';
import IdListDTO from 'models/dto/IdListDTO';
import { KPIControlDTO } from 'models/dto/kpi/kpiControlDTO';
import NormControlDTO from 'models/dto/normControlDTO';
import { ObjectiveControlDTO } from 'models/dto/Objective/objectiveControlDTO';
import { ProcessControlDTO } from 'models/dto/Process/processControlDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import AppError from 'utils/appError';
import { toApiDateTimeOptional } from 'utils/datetime';
import http from './httpService';

export async function apiGetControls(accessToken: string, globalDataCache: GlobalDataCache): Promise<Control[]> {
  try {
    const ar = await http.get<ControlDTO[]>(`/controls`, http.getRequestConfig(accessToken));

    return mapFromControls(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlsForStandard(
  isoNormId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control[]> {
  try {
    const res = await http.get<ControlDTO[]>(`/controls/standard/${isoNormId}`, http.getRequestConfig(accessToken));

    return mapFromControls(res.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlsForCustomStandard(
  normId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control[]> {
  try {
    const res = await http.get<ControlDTO[]>(`/controls/custom/${normId}`, http.getRequestConfig(accessToken));

    return mapFromControls(res.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlsForRisk(
  riskId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control[]> {
  try {
    const res = await http.get<ControlDTO[]>(`/controls/risk/${riskId}`, http.getRequestConfig(accessToken));

    return mapFromControls(res.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlsForTask(
  taskId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control[]> {
  try {
    const res = await http.get<ControlDTO[]>(`/controls/task/${taskId}`, http.getRequestConfig(accessToken));

    return mapFromControls(res.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlChildren(
  controlId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control[]> {
  try {
    const ar = await http.get<ControlDTO[]>(`/controls/${controlId}/children`, http.getRequestConfig(accessToken));

    return mapFromControls(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControl(
  controlId: number,
  includeISOControls: boolean,
  includePackageInstructions: boolean,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control | undefined> {
  try {
    if (!controlId || controlId < 0) return undefined;
    const ar = await http.get<ControlDTO>(
      `/controls/${controlId}?includeISOControls=${includeISOControls}&includePackageInstructions=${includePackageInstructions}`,
      http.getRequestConfig(accessToken),
    );
    if (!ar.data) return undefined;

    return mapFromControl(ar.data, globalDataCache) as Control;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlOrTheme(
  id: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control | undefined> {
  try {
    if (isNaN(id)) return undefined;
    const ar = await http.get<ControlDTO>(`/controls/themes/${id}`, http.getRequestConfig(accessToken));
    if (!ar.data) return undefined;

    return mapFromControl(ar.data, globalDataCache) as Control;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlCode(accessToken: string, code: string): Promise<number> {
  try {
    const ar = await http.post<number>(`/controls/code`, { valueString: code }, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddControl(
  control: Control,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control> {
  try {
    const controlDTO = mapToControl(control);
    const ar = await http.post<ControlDTO>(`/controls`, controlDTO, http.getRequestConfig(accessToken));

    return mapFromControl(ar.data, globalDataCache) as Control;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateControl(
  control: Control,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control> {
  try {
    const controlDTO: ControlDTO = mapToControl(control) as ControlDTO;
    const ar = await http.put<ControlDTO>('/controls', controlDTO, http.getRequestConfig(accessToken));

    return mapFromControl(ar.data, globalDataCache) as Control;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveControl(control: Control, accessToken: string): Promise<boolean> {
  try {
    await http.post(`/controls/delete/${control.controlId}`, null, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlScene(accessToken: string, globalDataCache: GlobalDataCache): Promise<ControlScene> {
  try {
    const ar = await http.get<ControlSceneDTO>(`/controls/scene`, http.getRequestConfig(accessToken));

    return mapFromControlScene(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlContext(
  controlId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<ControlContext> {
  try {
    const ar = await http.get<ControlContextDTO>(`/controls/context/${controlId}`, http.getRequestConfig(accessToken));

    return mapFromControlContext(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiSetCustomNorm(
  normId: number,
  controlId: number,
  add: boolean,
  accessToken: string,
): Promise<void> {
  try {
    const normControlDTO: NormControlDTO = new NormControlDTO(normId, controlId);
    await http.post(`/controls/norms/?add=${add}`, normControlDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveControlFromControl(
  controlIdFrom: number,
  controlIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new EntityRelationDTO();
    body.idFrom = controlIdFrom;
    body.idTo = controlIdTo;
    await http.post<EntityRelationDTO>(`/controls/control?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddControlToControl(
  controlIdFrom: number,
  controlIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new EntityRelationDTO();
    body.idFrom = controlIdFrom;
    body.idTo = controlIdTo;
    await http.post<EntityRelationDTO>(`/controls/control?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Objective
export async function apiAddObjectiveToControl(
  objectiveIdFrom: number,
  controlIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ObjectiveControlDTO(controlIdTo, objectiveIdFrom);
    await http.post<ObjectiveControlDTO>(`/objectives/control?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveObjectiveFromControl(
  objectiveIdFrom: number,
  controlId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ObjectiveControlDTO(controlId, objectiveIdFrom);
    await http.post<ObjectiveControlDTO>(`/objectives/control?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//process

export async function apiAddProcessToControl(
  processIdFrom: number,
  controlIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ProcessControlDTO(processIdFrom, controlIdTo);

    await http.post<ProcessControlDTO>(`/processes/control?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveProcessFromControl(
  processIdFrom: number,
  controlIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ProcessControlDTO(processIdFrom, controlIdTo);
    await http.post<ProcessControlDTO>(`/processes/control?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//KPI
export async function apiAddKPIToControl(
  kpiIdFrom: number,
  controlIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new KPIControlDTO(kpiIdFrom, controlIdTo);
    await http.post<KPIControlDTO>(`/kpis/control?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveKPIFromControl(
  kpiIdFrom: number,
  controlIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new KPIControlDTO(kpiIdFrom, controlIdTo);
    await http.post<KPIControlDTO>(`/kpis/control?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//dashboard
export async function apiCreateControlDashboard(
  controlId: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Dashboard> {
  try {
    const ar = await http.post<DashboardDTO>(
      `/controls/${controlId}/dashboard`,
      null,
      http.getRequestConfig(accessToken),
    );

    return mapFromDashboard(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetNextControl(
  controlId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control | undefined> {
  try {
    const ar = await http.get<ControlDTO>(`/controls/${controlId}/next`, http.getRequestConfig(accessToken));

    return mapFromControl(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetPrevControl(
  controlId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Control | undefined> {
  try {
    const ar = await http.get<ControlDTO>(`/controls/${controlId}/prev`, http.getRequestConfig(accessToken));

    return mapFromControl(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlInheritOptions(
  controlId: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Control[]> {
  try {
    const ar = await http.get<ControlDTO[]>(`/controls/${controlId}/inherit`, http.getRequestConfig(accessToken));

    return mapFromControls(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlInheritance(accessToken: string, cache: GlobalDataCache): Promise<Control[]> {
  try {
    const ar = await http.get<ControlDTO[]>(`/controls/inherit`, http.getRequestConfig(accessToken));

    return mapFromControls(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateControlInheritOptions(
  sourceControlId: number,
  orgUnitId: string,
  controlId: number,
  copyImpl: boolean,
  overwriteImpl: boolean,
  copyStat: boolean,
  copyAppl: boolean,
  copyCust: boolean,
  copyLib: boolean,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Control | undefined> {
  try {
    const dto = new ControlThemeInheritInfoDTO();
    dto.orgUnitId = orgUnitId;
    dto.id = controlId;
    dto.copyImplementation = copyImpl;
    dto.overwriteImplementation = overwriteImpl;
    dto.copyStatus = copyStat;
    dto.copyApplicability = copyAppl;
    dto.copyCustomStandards = copyCust;
    dto.copyLibraryItems = copyLib;

    const ar = await http.post<ControlDTO>(
      `/controls/${sourceControlId}/inherit`,
      dto,
      http.getRequestConfig(accessToken),
    );

    return mapFromControl(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlDashboard(
  controlId: number,
  startDate: Date | undefined,
  endDate: Date | undefined,
  accessToken: string,
): Promise<ControlDashboard> {
  try {
    const ar = await http.get<ControlDashboardDTO>(`/controls/${controlId}/dashboard`, {
      ...http.getRequestConfig(accessToken),
      params: {
        startDate: toApiDateTimeOptional(startDate),
        endDate: toApiDateTimeOptional(endDate),
      },
    });

    return mapFromControlDashboard(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetDedupControlCount(ids: string[], accessToken: string): Promise<number> {
  try {
    const dto = new IdListDTO(ids);
    const ar = await http.post<number>(`/controls/dedup/count`, dto, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetControlsOnly(accessToken: string, globalDataCache: GlobalDataCache): Promise<Control[]> {
  try {
    const ar = await http.get<ControlDTO[]>(`/controls?excludeIsoControls=true`, http.getRequestConfig(accessToken));
    
    return mapFromControls(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
